import { FC } from "react";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/lib/shadcn/ui/tooltip";
import { cn } from "@/lib/shadcn/utils";

// The single place a Tier-2 blank cell ships (airbnb-review-performance-table
// SC-005). An em-dash inside a focusable <button> styled as text so the reason
// is reachable by keyboard, wrapped in a Tooltip. The SAME reason copy is the
// button's aria-label, so a screen reader hears the reason, not "dash".

const EM_DASH = "—";

interface UnavailableCellProps {
    // A response reason code (see review_performance.go). Unknown codes fall back
    // to a generic "not available" message.
    reason: string;
    // Interpolated into the under_guard copy (EC-004).
    newReviews?: number;
    extraClasses?: string;
}

function reasonCopy(reason: string, newReviews?: number): string {
    switch (reason) {
        case "under_guard":
            return `There aren't enough new reviews this period (${newReviews ?? 0}) for a reliable cohort score at this listing's rating precision.`;
        case "count_decreased":
            return "The review count fell this period (Airbnb removed a review), so a cohort score cannot be derived.";
        case "category_missing":
            return "This category was not collected in an earlier snapshot.";
        case "no_baseline":
            return "No earlier snapshot exists for this apartment, so there is nothing to compare against.";
        case "no_comparison":
            return "No data exists for the comparison period.";
        case "zero_cohort":
            return "No new reviews arrived this period, so there is no cohort to average.";
        default:
            return "Not available for this period.";
    }
}

const UnavailableCell: FC<UnavailableCellProps> = ({ reason, newReviews, extraClasses }) => {
    const copy = reasonCopy(reason, newReviews);

    return (
        <Tooltip>
            <TooltipTrigger asChild>
                <button
                    type="button"
                    data-testid="unavailable-cell"
                    aria-label={copy}
                    // Styled as text, not a button. text-muted-foreground clears
                    // 4.5:1 including on a bg-muted/40 Tier-1 row (WCAG AA).
                    className={cn(
                        "cursor-help bg-transparent p-0 text-xs text-muted-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                        extraClasses,
                    )}
                >
                    {EM_DASH}
                </button>
            </TooltipTrigger>
            <TooltipContent>{copy}</TooltipContent>
        </Tooltip>
    );
};

export default UnavailableCell;
